import { useEffect } from "react";

const STRUCTURAL_BUTTONS = [
  ".nw-sort-button",
  ".lso-iconbtn",
  ".icon-action-btn",
  ".btn-close",
  ".app-window-close",
  ".sidebar-btn",
  ".sidebar-tab",
  ".sidebar-backdrop",
  ".mobile-subnav-toggle",
  ".mobile-admin-toggle",
  ".right-slide-panel-backdrop",
  ".right-slide-panel-close",
  ".user-avatar-trigger",
  ".user-profile-action-centre",
  ".activity-history-summary",
  ".activity-quick-actions button",
  ".activity-filters button",
  ".activity-document-chip button",
  ".my-account-section-header",
  ".page-link",
  ".nav-link",
  ".nw-styled-select-toggle",
  ".nw-styled-select-option",
  ".nw-date-range-filter__toggle",
  ".security-centre-tabs button",
  ".communications-tabs button",
  ".security-role-row",
  "[data-no-standard-button='true']",
].join(",");

const SAVE_LABEL = /^(save|saving)(?:\b|\s|\.{3}|…)/i;

const RETURN_LABEL = /^return\b/i;
const CLOSE_LABEL = /^close(?:\s+(?:window|panel|dialog))?$/i;
const YES_NO_LABELS = new Set(["yes", "no", "unknown", "n/a", "not applicable"]);

function decorateChoiceGroups(root = document) {
  const groups = [];
  if (root instanceof Element && root.matches("[role='group'], .btn-group")) {
    groups.push(root);
  }
  root.querySelectorAll?.("[role='group'], .btn-group").forEach((group) => groups.push(group));

  groups.forEach((group) => {
    const radios = Array.from(group.children).filter(
      (child) => child instanceof HTMLInputElement && child.type === "radio",
    );
    const buttons = Array.from(group.children).filter(
      (child) => child instanceof HTMLButtonElement,
    );
    const values = radios.map((radio) => String(radio.value).trim().toLowerCase());
    const labels = buttons.map((button) => String(button.textContent || "").trim().toLowerCase());
    const radioYesNo = values.some((value) => value === "yes" || value === "1")
      && values.some((value) => value === "no" || value === "0");
    const buttonYesNo = labels.includes("yes") && labels.includes("no")
      && labels.every((label) => YES_NO_LABELS.has(label));
    group.classList.toggle("nw-yesno-radio-group", radioYesNo);
    group.classList.toggle("nw-yesno-button-group", buttonYesNo);
  });
}

function decorateButton(button) {
  if (!(button instanceof HTMLButtonElement)) return;

  if (STRUCTURAL_BUTTONS && button.matches(STRUCTURAL_BUTTONS)) {
    button.classList.remove(
      "app-compact-button",
      "app-icon-button",
      "app-save-button",
      "app-save-has-icon",
      "app-return-button",
      "app-close-action",
    );
    return;
  }

  const label = String(button.innerText || button.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
  const isIconOnly = !label && Boolean(button.querySelector("i, svg, img"));

  button.classList.toggle("app-icon-button", isIconOnly);
  button.classList.toggle("app-compact-button", !isIconOnly);
  button.classList.toggle("app-return-button", RETURN_LABEL.test(label));
  button.classList.toggle("app-close-action", CLOSE_LABEL.test(label));

  if (!SAVE_LABEL.test(label)) {
    button.classList.remove("app-save-button", "app-save-has-icon");
    return;
  }

  button.classList.add("app-save-button");
  button.classList.toggle(
    "app-save-has-icon",
    Boolean(
      button.querySelector(
        "i.fa-save, i.fa-floppy-disk, i.fa-floppy-disk-o, svg, [data-save-icon='true']",
      ),
    ),
  );
}

function enhanceButtons(root = document) {
  decorateChoiceGroups(root);
  if (root instanceof HTMLButtonElement) decorateButton(root);
  root.querySelectorAll?.("button").forEach(decorateButton);
}

export default function GlobalButtonEnhancer() {
  useEffect(() => {
    enhanceButtons();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") {
          const button = mutation.target.parentElement?.closest("button");
          if (button) decorateButton(button);
          return;
        }
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) enhanceButtons(node);
        });
        const changedButton =
          mutation.target instanceof Element
            ? mutation.target.closest("button")
            : null;
        if (changedButton) decorateButton(changedButton);
      });
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
